/**
 * Durations and deadlines, worded the way a person reads them.
 *
 * Two rules, both learned from the analytics work:
 *
 * * **Coarse, not precise.** "2 days" is what somebody needs to decide what to
 *   do next; "2 days 3 hours 14 minutes" is the same fact costing more to read.
 * * **No plural suffixes.** i18next here uses English source strings as keys
 *   with no plural handling (CLAUDE.md invariant 7), so a count of one needs
 *   its own key -- "1 day", never "1 days".
 */
import type { TFunction } from "i18next";

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** "3 hours", "2 days", "just now". Always non-negative -- see `dueIn`. */
export function humanDuration(seconds: number, t: TFunction): string {
  const s = Math.max(0, Math.round(seconds));
  if (s < MINUTE) return t("just now");
  if (s < HOUR) {
    const n = Math.floor(s / MINUTE);
    return n === 1 ? t("1 minute") : t("{{count}} minutes", { count: n });
  }
  if (s < DAY) {
    const n = Math.floor(s / HOUR);
    return n === 1 ? t("1 hour") : t("{{count}} hours", { count: n });
  }
  const n = Math.floor(s / DAY);
  return n === 1 ? t("1 day") : t("{{count}} days", { count: n });
}

/**
 * "Due in 3 hours" / "3 hours late" / "" when there is no deadline.
 *
 * An absent deadline renders as nothing rather than as "no due date": a column
 * of that phrase says less than an empty cell, and most steps have no SLA.
 */
export function dueLabel(dueAt: string | null, t: TFunction): string {
  if (!dueAt) return "";
  const delta = (new Date(dueAt).getTime() - Date.now()) / 1000;
  return delta < 0
    ? t("{{time}} late", { time: humanDuration(-delta, t) })
    : t("in {{time}}", { time: humanDuration(delta, t) });
}
